import { useCallback, useRef, useState } from 'react';
import { UserRoles } from '@/enums/enums';
import { useDeleteContractCategoryMutation } from '@/features/contract-category/contract-category.mutations';
import { useContractCategories } from '@/features/contract-category/contract-category.queries';
import { getUserOptions } from '@/features/user/user.queries';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { DataTableRowActions } from '@/components/datatable/data-table-row-action';
import { TableDateColumn } from '@/components/datatable/data-table-simple/data-table-custom-columns';
import { DataTableSimple } from '@/components/datatable/data-table-simple/data-table-simple';
import AlertModal from '@/components/modals/alert-modal';
import ContractCategoryCreateUpdateModal from '@/components/modals/contract-category-create-update-modal';

const ContractSettingsRouteSchema = z.object({
  mode: z.enum(['create', 'update']).optional(),
  category: z.number().optional(),
});

export const Route = createFileRoute('/_app/(contracts)/contracts/settings')({
  validateSearch: zodSearchValidator(ContractSettingsRouteSchema),
  component: StudentSettingsPage,
  beforeLoad: async ({ context }) => {
    const { user } = await context.queryClient.ensureQueryData(getUserOptions);

    if (![UserRoles.Root, UserRoles.Admin].includes(user?.role as UserRoles)) {
      throw redirect({
        to: '/',
      });
    }
  },
});

function StudentSettingsPage() {
  return (
    <div className='flex h-full flex-col gap-y-2 px-4 py-2'>
      <ContractsCategoryHeader />
      <ContractCategoryTable />
    </div>
  );
}

function ContractsCategoryHeader() {
  const navigate = useNavigate();
  const { t } = useTranslation(['contract']);

  const onCreateNewCustomField = () => {
    navigate({
      to: '.',
      search: { mode: 'create' },
    });
  };

  return (
    <div className='flex flex-row justify-between py-4'>
      <div className='flex flex-col'>
        <span className='text-xl font-bold'>
          {t('settings.categories.page_title', { format: 'uppercase' })}
        </span>
        <span className='text-base font-light text-neutral-500'>
          {t('settings.categories.page_sub_title')}
        </span>
      </div>
      <div className='flex flex-row space-x-4'>
        <Button className='flex items-center gap-3' onClick={onCreateNewCustomField} size='default'>
          <Plus strokeWidth={4} className='h-6 w-6' />
          {t('settings.categories.contract_category')}{' '}
        </Button>
      </div>
      <ContractCategoryCreateUpdateModal />
    </div>
  );
}

function ContractCategoryTable() {
  const { t } = useTranslation(['table']);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const selectedDeleteId = useRef<number>(0);

  const navigate = useNavigate();
  const { data = [], isLoading } = useContractCategories();

  const deleteMutation = useDeleteContractCategoryMutation();

  const onEditContractCategory = (id: number) => {
    navigate({
      to: '.',
      search: { mode: 'update', category: id },
    });
  };

  const deleteContractCategory = (id: number) => {
    selectedDeleteId.current = id;
    setDeleteOpen(true);
  };

  const onSuccessDelete = useCallback(async () => {
    await deleteMutation.mutateAsync(selectedDeleteId.current);
  }, [deleteMutation]);

  return (
    <>
      <DataTableSimple
        data={data}
        columns={['name', 'createdAt', 'updatedAt']}
        columnsLabels={{
          name: t('name'),
          createdAt: t('created_at'),
          updatedAt: t('updated_at'),
        }}
        extraColumns={[
          {
            id: 'actions',
            size: 50,
            cell: ({ row }) => (
              <DataTableRowActions
                row={row}
                menuItems={[
                  {
                    label: 'Edit',
                    onClick: () => onEditContractCategory(row.original.id),
                  },
                  {
                    label: 'Delete',
                    onClick: () => deleteContractCategory(row.original.id),
                  },
                ]}
              />
            ),
          },
        ]}
        customRender={{
          createdAt: (row) => <TableDateColumn date={row.getValue('createdAt')} isISO />,
          updatedAt: (row) => <TableDateColumn date={row.getValue('updatedAt')} isISO />,
        }}
        isLoading={isLoading}
        className='flex h-full flex-col overflow-y-auto py-4'
      />
      <AlertModal
        title={'Are you absolutely sure?'}
        description={
          'This action cannot be undone. This will permanently delete the selected dynamic properties.'
        }
        setOpen={setDeleteOpen}
        isOpen={isDeleteOpen}
        onSuccess={onSuccessDelete}
      />
    </>
  );
}
